
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ShoppingCart, Language, ActiveShoppingCart, Ingredient } from '../types';
import XIcon from './icons/XIcon';
import CartIcon from './icons/CartIcon';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';
import ExportIcon from './icons/ExportIcon'; 
import PlusIcon from './icons/PlusIcon';
import MeatIcon from './icons/MeatIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ListBulletIcon from './icons/ListBulletIcon';
import ConfirmationModal from './ConfirmationModal';
import SmartUnitCapsule from './SmartUnitCapsule';
import { getTranslation } from '../utils/translations';
import { formatIngredientQuantity } from '../utils/unitConversion';
import { getSectionDisplayName, normalizeSectionKey } from '../utils/sectionNames';
import { toListPillarId } from '../utils/shoppingCategories';

interface ShoppingCartDetailsModalProps {
    cart: ShoppingCart;
    onClose: () => void;
    onEdit: () => void;
    language?: Language;
    allCarts?: ShoppingCart[];
    activeCart?: ActiveShoppingCart | null;
    onUpdateActiveCart?: (cart: ActiveShoppingCart) => Promise<void>;
    onMoveItems?: (ingredients: Ingredient[], fromCartId: string, toCartId: string) => Promise<void>;
    onCreateAndMoveItems?: (ingredients: Ingredient[], fromCartId: string, newCartName: string) => Promise<void>;
    onDeleteItems?: (cartId: string, ingredientNames: string[]) => Promise<void>;
    onUpdateCartIngredients?: (cartId: string, ingredients: Ingredient[]) => Promise<void>;
    onDeleteCart?: (cartId: string) => Promise<void> | void;
}

const STANDARD_UNITS = ['kg', 'g', 'unit', 'ml', 'liter', 'tsp', 'tbsp', 'cup', 'container', 'tub', 'can', 'bottle', 'bag', 'box', 'roll', 'to taste', 'pinch', 'bunch', 'package', 'clove', 'head', 'stalk', 'shot'];

// --- Helper Components ---

const SectionChooserModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    sections: string[];
    onConfirm: (sectionName: string) => void;
    language?: Language;
}> = ({ isOpen, onClose, sections, onConfirm, language }) => {
    const t = useCallback((key: any) => getTranslation(language, key), [language]);
    const [selectedSection, setSelectedSection] = useState<string | null>(null);
    const [newSection, setNewSection] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedSection(null);
            setNewSection('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        const target = newSection.trim() || selectedSection;
        if (target) {
            onConfirm(target);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[500] p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-md flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 animate-scale-in transition-colors" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b bg-slate-50 dark:bg-slate-800/50 dark:border-slate-800 flex justify-between items-center">
                    <div>
                        <h3 className="font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight text-base">{t('moveToSection')}</h3>
                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('organizeList')}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-800 dark:hover:text-slate-200 transition-colors bg-white dark:bg-slate-800 rounded-full shadow-sm"><XIcon className="w-5 h-5" /></button>
                </div>
                
                <div className="p-6 space-y-3 flex-grow overflow-y-auto max-h-[50vh] scrollbar-none">
                    <div className="grid grid-cols-1 gap-2">
                        {sections.map(section => (
                            <button 
                                key={section} 
                                onClick={() => { setSelectedSection(section); setNewSection(''); }}
                                className={`w-full p-4 border rounded-2xl transition-all text-start font-bold flex items-center gap-3 active:scale-[0.98] ${selectedSection === section ? 'bg-sky-50 dark:bg-sky-900/30 border-sky-400 dark:border-sky-500 shadow-sm' : 'bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-sky-200 dark:hover:border-sky-700'}`}
                            >
                                <div className={`w-3 h-3 rounded-full border-2 ${selectedSection === section ? 'bg-sky-500 border-sky-500' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'}`}></div>
                                <span className={`truncate text-sm font-black uppercase tracking-tight ${selectedSection === section ? 'text-sky-800 dark:text-sky-400' : 'text-slate-800 dark:text-slate-200'}`}>{getSectionDisplayName(section, t)}</span>
                            </button>
                        ))}
                    </div>
                    
                    <div className="pt-6 border-t border-slate-100 dark:border-slate-800 mt-4 space-y-3">
                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] px-1">{t('orCreateNewSection')}</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={newSection} 
                                onChange={e => { setNewSection(e.target.value); setSelectedSection(null); }}
                                placeholder={t('newSectionNamePlaceholder')}
                                className="flex-grow p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none font-bold text-sm shadow-inner transition-all text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                            />
                        </div>
                    </div>
                </div>
                
                <div className="p-6 bg-slate-50 dark:bg-slate-900 border-t dark:border-slate-800 flex flex-col gap-3">
                    <button 
                        onClick={handleConfirm}
                        disabled={!selectedSection && !newSection.trim()}
                        className="w-full py-4 bg-slate-900 dark:bg-slate-700 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-slate-200 dark:shadow-slate-900/50 active:scale-95 disabled:opacity-20 transition-all"
                    >
                        {t('confirmMove')}
                    </button>
                    <button onClick={onClose} className="text-slate-400 dark:text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-700 dark:hover:text-slate-300 transition-colors py-2">{t('cancel')}</button>
                </div>
            </div>
        </div>
    );
};

const ListChooserModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    carts: ShoppingCart[];
    currentCartId: string;
    onConfirm: (targetCartId: string | null, newCartName?: string) => void;
    language?: Language;
}> = ({ isOpen, onClose, carts, currentCartId, onConfirm, language }) => {
    const t = useCallback((key: any) => getTranslation(language, key), [language]);
    const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
    const [newCartName, setNewCartName] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedCartId(null);
            setNewCartName('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const otherCarts = carts.filter(c => c.id !== currentCartId && !c.isDeleted);

    const handleConfirm = () => {
        if (newCartName.trim()) {
            onConfirm(null, newCartName.trim());
        } else if (selectedCartId) {
            onConfirm(selectedCartId);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[500] p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-md flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 animate-scale-in transition-colors" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b bg-slate-50 dark:bg-slate-800/50 dark:border-slate-800 flex justify-between items-center">
                    <div>
                        <h3 className="font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight text-base">{t('moveToList')}</h3>
                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('selectTargetList')}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-800 dark:hover:text-slate-200 transition-colors bg-white dark:bg-slate-800 rounded-full shadow-sm"><XIcon className="w-5 h-5" /></button>
                </div>
                
                <div className="p-6 space-y-3 flex-grow overflow-y-auto max-h-[50vh] scrollbar-none">
                    <div className="grid grid-cols-1 gap-2">
                        {otherCarts.map(cart => (
                            <button 
                                key={cart.id} 
                                onClick={() => { setSelectedCartId(cart.id); setNewCartName(''); }}
                                className={`w-full p-4 border rounded-2xl transition-all text-start font-bold flex items-center gap-3 active:scale-[0.98] ${selectedCartId === cart.id ? 'bg-sky-50 dark:bg-sky-900/30 border-sky-400 dark:border-sky-500 shadow-sm' : 'bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-sky-200 dark:hover:border-sky-700'}`}
                            >
                                <div className={`w-3 h-3 rounded-full border-2 ${selectedCartId === cart.id ? 'bg-sky-500 border-sky-500' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600'}`}></div>
                                <span className={`truncate text-sm font-black uppercase tracking-tight ${selectedCartId === cart.id ? 'text-sky-800 dark:text-sky-400' : 'text-slate-800 dark:text-slate-200'}`}>{cart.name}</span>
                            </button>
                        ))}
                        {otherCarts.length === 0 && <p className="text-center py-4 text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('noOtherLists')}</p>}
                    </div>
                    
                    <div className="pt-6 border-t border-slate-100 dark:border-slate-800 mt-4 space-y-3">
                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] px-1">{t('orCreateNewList')}</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={newCartName} 
                                onChange={e => { setNewCartName(e.target.value); setSelectedCartId(null); }}
                                placeholder={t('newListNamePlaceholder')}
                                className="flex-grow p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none font-bold text-sm shadow-inner transition-all text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                            />
                        </div>
                    </div>
                </div>
                
                <div className="p-6 bg-slate-50 dark:bg-slate-900 border-t dark:border-slate-800 flex flex-col gap-3">
                    <button 
                        onClick={handleConfirm}
                        disabled={!selectedCartId && !newCartName.trim()}
                        className="w-full py-4 bg-slate-900 dark:bg-slate-700 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-slate-200 dark:shadow-slate-900/50 active:scale-95 disabled:opacity-20 transition-all"
                    >
                        {t('confirmMove')}
                    </button>
                    <button onClick={onClose} className="text-slate-400 dark:text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-700 dark:hover:text-slate-300 transition-colors py-2">{t('cancel')}</button>
                </div>
            </div>
        </div>
    );
};

const EditableSectionHeader: React.FC<{
    name: string;
    itemCount: number;
    onRename: (newName: string) => void;
    isCollapsed: boolean;
    onToggle: () => void;
    onDelete?: () => void;
    t: (k: any) => string;
}> = ({ name, itemCount, onRename, isCollapsed, onToggle, onDelete, t }) => {
    const displayName = getSectionDisplayName(name, t);
    const [localName, setLocalName] = useState(displayName);

    useEffect(() => {
        setLocalName(getSectionDisplayName(name, t));
    }, [name, t]);

    const handleBlur = () => {
        const currentTranslated = getSectionDisplayName(name, t);
        if (localName.trim() && localName !== currentTranslated) {
            onRename(localName.trim());
        } else {
            setLocalName(currentTranslated);
        }
    };

    return (
        <div className="flex justify-between items-center p-6 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer group" onClick={onToggle}>
            <div className="flex items-center gap-3 flex-grow overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className={`text-slate-300 dark:text-slate-600 transition-transform duration-300 group-hover:text-slate-500 dark:group-hover:text-slate-400 shrink-0 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={onToggle}>
                    <ChevronDownIcon className="w-5 h-5" />
                </div>
                <input 
                    type="text" 
                    value={localName} 
                    onChange={(e) => setLocalName(e.target.value)} 
                    onBlur={handleBlur}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as any).blur()}
                    className="bg-transparent border-none focus:ring-0 p-0 text-base font-black text-brand-orange uppercase tracking-tight w-full focus:outline-none min-w-0" 
                    placeholder={t('sectionNamePlaceholder')} 
                />
                <div className="shrink-0 px-2">
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full shadow-sm border border-slate-100 dark:border-slate-700">
                        {itemCount}
                    </span>
                </div>
            </div>
            {onDelete && (
                <div className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"><TrashIcon className="w-4 h-4" /></button>
                </div>
            )}
        </div>
    );
};

const SelectionSectionHeader: React.FC<{
    name: string;
    itemCount: number;
    isCollapsed: boolean;
    onToggle: () => void;
    allSelected: boolean;
    onToggleSelectAll: () => void;
    t: (k: any) => string;
}> = ({ name, itemCount, isCollapsed, onToggle, allSelected, onToggleSelectAll, t }) => {
    const displayName = getSectionDisplayName(name, t);

    return (
        <div className="flex justify-between items-center p-6 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer group" onClick={onToggle}>
            <div className="flex items-center gap-3 flex-grow overflow-hidden min-w-0">
                <div className={`text-slate-300 dark:text-slate-600 transition-transform duration-300 group-hover:text-slate-500 dark:group-hover:text-slate-400 shrink-0 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`}>
                    <ChevronDownIcon className="w-5 h-5" />
                </div>
                <span className="text-base font-black text-brand-orange uppercase tracking-tight truncate">
                    {displayName}
                </span>
                <div className="shrink-0 px-2">
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full shadow-sm border border-slate-100 dark:border-slate-700">
                        {itemCount}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={onToggleSelectAll}
                    className="text-[9px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 transition-colors px-1"
                >
                    {allSelected ? t('deselectAll') : t('selectAll')}
                </button>
            </div>
        </div>
    );
};

// Component to handle Ingredient Name editing with local state and validation
const IngredientNameInput: React.FC<{
    value: string;
    onSave: (newName: string) => void;
    onValidate: (newName: string) => string | null;
    placeholder: string;
    inputRef?: React.Ref<HTMLInputElement>;
    onEnter?: () => void;
}> = ({ value, onSave, onValidate, placeholder, inputRef, onEnter }) => {
    const [val, setVal] = useState(value);

    useEffect(() => {
        setVal(value);
    }, [value]);

    const handleCommit = () => {
        const trimmed = val.trim();
        if (trimmed === value) return; // No change

        const errorMsg = onValidate(trimmed);
        if (errorMsg) {
            alert(errorMsg);
            setVal(value); // Revert
            return;
        }
        onSave(trimmed);
    };

    return (
        <input 
            ref={inputRef}
            type="text"
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={handleCommit}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.currentTarget.blur(); // Triggers handleCommit
                    if (onEnter) setTimeout(onEnter, 0); // Next tick to allow save first
                }
            }}
            className="w-full bg-transparent border-none focus:ring-0 p-0 text-sm font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
            placeholder={placeholder}
        />
    );
};

// --- Main Component ---

const ShoppingCartDetailsModal: React.FC<ShoppingCartDetailsModalProps> = ({ 
    cart, onClose, onEdit, language, allCarts = [], activeCart, onUpdateActiveCart, onMoveItems, onCreateAndMoveItems, onDeleteItems, onUpdateCartIngredients, onDeleteCart
}) => {
    const t = useMemo(() => (key: any) => getTranslation(language, key), [language]);
    
    const [viewMode, setViewMode] = useState<'view' | 'select' | 'sync'>('view');
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set<string>());
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSectionChooserOpen, setIsSectionChooserOpen] = useState(false);
    const [isListChooserOpen, setIsListChooserOpen] = useState(false);
    const [singleTargetIdx, setSingleTargetIdx] = useState<number | null>(null);
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
    const [confirmAction, setConfirmAction] = useState<{ type: 'delete_items' | 'delete_section', target?: string } | null>(null);
    const [showDeleteCartConfirm, setShowDeleteCartConfirm] = useState(false);
    
    const ingredientInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
    const ingredients = cart.ingredients ?? [];

    const groupedIngredients = useMemo(() => {
        const groups: Record<string, { ing: Ingredient; originalIndex: number }[]> = {};
        ingredients.forEach((ing, idx) => {
            const section = normalizeSectionKey(ing.section, t);
            if (!groups[section]) groups[section] = [];
            groups[section].push({ ing, originalIndex: idx });
        });
        return Object.entries(groups) as [string, { ing: Ingredient; originalIndex: number }[]][];
    }, [ingredients, t]);

    // Collapse all sections by default when opening a different cart
    useEffect(() => {
        const sections = new Set<string>();
        ingredients.forEach((ing) => {
            sections.add(normalizeSectionKey(ing.section, t));
        });
        setCollapsedSections(sections);
    }, [cart.id]);

    const availableSections = useMemo(() => {
        const sections = new Set<string>();
        ingredients.forEach(i => sections.add(normalizeSectionKey(i.section, t)));
        return Array.from(sections);
    }, [ingredients, t]);

    const handleEnterSyncMode = () => {
        const preSelected = new Set<string>();
        const listPillarId = toListPillarId(cart.id);
        ingredients.forEach((ing) => {
            const existsInActive = activeCart?.items.some(i => 
                i.name.toLowerCase().trim() === ing.name.toLowerCase().trim() &&
                (i.category || '') === listPillarId &&
                !i.done
            );
            if (existsInActive) preSelected.add(ing.name);
        });
        setSelectedItems(preSelected);
        setViewMode('sync');
    };

    const handleEnterSelectMode = () => {
        setSelectedItems(new Set());
        setViewMode('select');
    };

    const handleCancelMode = () => {
        setSelectedItems(new Set());
        setSingleTargetIdx(null);
        setViewMode('view');
        setConfirmAction(null);
    };

    const toggleItem = (name: string) => {
        setSelectedItems(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const toggleSectionVisibility = (name: string) => {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const allSectionsExpanded = groupedIngredients.length > 0
        && groupedIngredients.every(([name]) => !collapsedSections.has(name));

    const toggleAllSections = () => {
        if (allSectionsExpanded) {
            setCollapsedSections(new Set(groupedIngredients.map(([name]) => name)));
        } else {
            setCollapsedSections(new Set());
        }
    };

    const isSectionFullySelected = (items: { ing: Ingredient }[]) =>
        items.length > 0 && items.every(({ ing }) => selectedItems.has(ing.name));

    const toggleSelectSection = (items: { ing: Ingredient }[]) => {
        const sectionItemNames = items.map(({ ing }) => ing.name);
        const allSelected = sectionItemNames.every(name => selectedItems.has(name));

        setSelectedItems(prev => {
            const next = new Set(prev);
            if (allSelected) {
                sectionItemNames.forEach(name => next.delete(name));
            } else {
                sectionItemNames.forEach(name => next.add(name));
            }
            return next;
        });
    };

    const handlePerformSync = async () => {
        if (!activeCart || !onUpdateActiveCart) return;
        setIsProcessing(true);
        try {
            let nextItems = [...activeCart.items];
            const listPillarId = toListPillarId(cart.id);
            
            ingredients.forEach((ing) => {
                const ingNameClean = ing.name.toLowerCase().trim();
                const existingIdx = nextItems.findIndex(i => 
                    i.name.toLowerCase().trim() === ingNameClean &&
                    (i.category || '') === listPillarId
                );

                const isSelected = selectedItems.has(ing.name);

                if (isSelected) {
                    if (existingIdx > -1) {
                        if (nextItems[existingIdx].done) {
                            nextItems[existingIdx] = { ...nextItems[existingIdx], done: false };
                        }
                    } else {
                        const itemId = `${ingNameClean}_${ing.unit || 'unit'}_${listPillarId}`;
                        nextItems.push({
                            id: itemId,
                            name: ing.name.trim(),
                            unit: ing.unit || 'unit',
                            isMeat: ing.isMeat,
                            done: false,
                            category: listPillarId,
                            sources: [{
                                id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                                originName: cart.name,
                                quantity: ing.quantity,
                                originType: 'list'
                            }]
                        });
                    }
                } else {
                    if (existingIdx > -1 && !nextItems[existingIdx].done) {
                        nextItems.splice(existingIdx, 1);
                    }
                }
            });

            await onUpdateActiveCart({ ...activeCart, items: nextItems });
            handleCancelMode();
            onClose();
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfirmedBulkDelete = async () => {
        if (!onDeleteItems) return;
        setIsProcessing(true);
        let closing = false;
        try {
            const namesToDelete = singleTargetIdx !== null 
                ? [ingredients[singleTargetIdx].name] 
                : Array.from(selectedItems);
            
            await onDeleteItems(cart.id, namesToDelete);
            
            const remainingCount = ingredients.length - namesToDelete.length;
            if (remainingCount <= 0) {
                closing = true;
                onClose();
                return;
            }
            handleCancelMode();
        } finally {
            if (!closing) {
                setIsProcessing(false);
                setConfirmAction(null);
            }
        }
    };

    const handleConfirmedSectionDelete = async () => {
        if (!onUpdateCartIngredients || !confirmAction?.target) return;
        const targetSection = confirmAction.target;
        setIsProcessing(true);
        let closing = false;
        try {
            const nextIngs = ingredients.filter(i => normalizeSectionKey(i.section, t) !== targetSection);
            await onUpdateCartIngredients(cart.id, nextIngs);
            if (nextIngs.length === 0) {
                closing = true;
                onClose();
                return;
            }
            handleCancelMode();
        } finally {
            if (!closing) {
                setIsProcessing(false);
                setConfirmAction(null);
            }
        }
    };

    const handleMoveToSection = async (sectionName: string) => {
        if (!onUpdateCartIngredients) return;
        setIsProcessing(true);
        try {
            const targets = singleTargetIdx !== null 
                ? [ingredients[singleTargetIdx].name] 
                : Array.from(selectedItems);
            
            if (targets.length === 0) return;

            const nextIngs = ingredients.map(ing => {
                if (targets.includes(ing.name)) {
                    return { ...ing, section: sectionName };
                }
                return ing;
            });

            await onUpdateCartIngredients(cart.id, nextIngs);
            
            setIsSectionChooserOpen(false);
            setSingleTargetIdx(null);
            handleCancelMode();
        } finally {
            setIsProcessing(false);
        }
    };

    const handleMoveToList = async (targetCartId: string | null, newCartName?: string) => {
        setIsProcessing(true);
        let closing = false;
        try {
            const namesToMove = singleTargetIdx !== null 
                ? [ingredients[singleTargetIdx].name] 
                : Array.from(selectedItems);
            
            const ingsToMove = ingredients.filter(i => namesToMove.includes(i.name));
            if (ingsToMove.length === 0) return;

            if (newCartName) {
                if (!onCreateAndMoveItems) return;
                await onCreateAndMoveItems(ingsToMove, cart.id, newCartName);
            } else if (targetCartId) {
                if (!onMoveItems) return;
                await onMoveItems(ingsToMove, cart.id, targetCartId);
            } else {
                return;
            }

            setIsListChooserOpen(false);
            setSingleTargetIdx(null);
            
            if (ingredients.length === namesToMove.length) {
                closing = true;
                onClose();
                return;
            }
            handleCancelMode();
        } finally {
            if (!closing) {
                setIsProcessing(false);
            }
        }
    };

    const handleRenameSection = async (oldName: string, newName: string) => {
        if (!onUpdateCartIngredients) return;
        const nextIngs = ingredients.map(i => {
            const currentSec = normalizeSectionKey(i.section, t);
            return currentSec === oldName ? { ...i, section: newName } : i;
        });
        await onUpdateCartIngredients(cart.id, nextIngs);
    };

    const handleUpdateIngredient = async (idx: number, updates: Partial<Ingredient>) => {
        if (!onUpdateCartIngredients) return;
        const nextIngs = [...ingredients];
        nextIngs[idx] = { ...nextIngs[idx], ...updates };
        await onUpdateCartIngredients(cart.id, nextIngs);
    };

    const validateIngredientName = (name: string, currentIndex: number): string | null => {
        const lower = name.toLowerCase().trim();
        if (!lower) return null;
        
        const duplicate = ingredients.find((ing, idx) => idx !== currentIndex && ing.name.toLowerCase().trim() === lower);
        if (duplicate) {
            const existingSection = normalizeSectionKey(duplicate.section, t);
            return `${t('limitReached')}: ${t('ingredientDuplicateInSection')
                .replace('{name}', name)
                .replace('{section}', getSectionDisplayName(existingSection, t))}`;
        }
        return null;
    };

    const handleConfirmedDeleteCart = async () => {
        if (!onDeleteCart) return;
        setIsProcessing(true);
        let closing = false;
        try {
            await onDeleteCart(cart.id);
            closing = true;
            setShowDeleteCartConfirm(false);
            onClose();
        } catch (error) {
            console.error('Failed to delete shopping cart', error);
            setShowDeleteCartConfirm(false);
        } finally {
            if (!closing) {
                setIsProcessing(false);
            }
        }
    };

    const isSelectionEnabled = viewMode !== 'view';
    const hideSectionInViewMode = viewMode === 'view' && groupedIngredients.length === 1;

    const renderIngredientRows = (items: { ing: Ingredient; originalIndex: number }[]) => (
        <ul className="divide-y divide-slate-50 dark:divide-slate-800">
            {items.map(({ ing, originalIndex }) => {
                const isSelected = selectedItems.has(ing.name);

                return (
                    <li
                        key={originalIndex}
                        onClick={() => isSelectionEnabled && toggleItem(ing.name)}
                        className={`
                            flex items-center justify-between p-3 transition-all
                            ${isSelected ? 'bg-sky-50/30 dark:bg-sky-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}
                            ${isSelectionEnabled ? 'cursor-pointer' : ''}
                        `}
                    >
                        <div className="flex items-center gap-3 flex-grow min-w-0">
                            {isSelectionEnabled && (
                                <div className={`
                                    w-5 h-5 rounded border transition-all flex items-center justify-center flex-shrink-0
                                    ${isSelected ? 'bg-sky-500 border-sky-500 text-white' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'}
                                `}>
                                    {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                                </div>
                            )}

                            <div className="flex-grow min-w-0">
                                {!isSelectionEnabled ? (
                                    <IngredientNameInput
                                        inputRef={(el: HTMLInputElement | null) => { if (el) ingredientInputRefs.current[originalIndex] = el; }}
                                        value={ing.name}
                                        onSave={newName => handleUpdateIngredient(originalIndex, { name: newName })}
                                        onValidate={newName => validateIngredientName(newName, originalIndex)}
                                        placeholder={t('ingredientNamePlaceholder')}
                                    />
                                ) : (
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate block">
                                        {ing.name}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 pl-2 shrink-0">
                            {!isSelectionEnabled ? (
                                <SmartUnitCapsule
                                    value={ing.quantity}
                                    unit={ing.unit || 'unit'}
                                    onChange={(val, unit) => handleUpdateIngredient(originalIndex, { quantity: val, unit })}
                                    language={language}
                                    isMeat={ing.isMeat}
                                    availableUnits={STANDARD_UNITS}
                                    bgClass="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                                />
                            ) : (
                                <div className="text-xs font-bold text-slate-400 dark:text-slate-500">
                                    {formatIngredientQuantity(ing.quantity, ing.unit || 'unit', t)}
                                </div>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] relative overflow-hidden transition-colors" onClick={e => e.stopPropagation()}>
                {isProcessing && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 backdrop-blur-[2px] z-[600] flex items-center justify-center">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-sky-600 rounded-full animate-spin"></div>
                            <span className="text-xs font-black text-slate-50 dark:text-slate-400 uppercase tracking-widest">{t('updatingList')}</span>
                        </div>
                    </div>
                )}

                <div className="p-6 border-b flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50 dark:border-slate-800">
                    <div className="flex items-center gap-4">
                        <div className="bg-sky-100 dark:bg-sky-900/30 p-2.5 rounded-2xl text-sky-600 dark:text-sky-400 shadow-sm">
                            <CartIcon className="h-6 w-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight leading-none mb-1">{cart.name}</h2>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{ingredients.length} {t('ingredients')}</span>
                                {cart.category && (
                                    <span className="text-[10px] bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 px-1.5 py-0.5 rounded-md font-black uppercase tracking-tighter">
                                        {t(cart.category.toLowerCase() as any)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {viewMode === 'view' && (
                            <>
                                {onDeleteCart && !cart.isMisc && (
                                    <button 
                                        onClick={() => setShowDeleteCartConfirm(true)}
                                        className="p-2 text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-white dark:hover:bg-slate-800 rounded-xl transition-all shadow-sm"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                )}
                                <button onClick={onEdit} className="p-2 text-slate-400 dark:text-slate-500 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-white dark:hover:bg-slate-800 rounded-xl transition-all shadow-sm">
                                    <PencilIcon className="w-5 h-5" />
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                            <XIcon className="h-8 w-8" />
                        </button>
                    </div>
                </div>

                <div className="px-6 py-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                    <div className="flex gap-4 items-center">
                        {viewMode === 'view' ? (
                            <button 
                                onClick={handleEnterSelectMode}
                                className="text-[11px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 transition-colors"
                            >
                                {t('select')}
                            </button>
                        ) : (
                            <button 
                                onClick={handleCancelMode}
                                className="text-[11px] font-black uppercase tracking-widest text-brand-orange"
                            >
                                {t('cancel')}
                            </button>
                        )}
                    </div>

                    {viewMode === 'view' && groupedIngredients.length > 1 && (
                        <button
                            type="button"
                            onClick={toggleAllSections}
                            className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        >
                            {allSectionsExpanded ? t('collapseAll') : t('expandAll')}
                        </button>
                    )}
                    
                    {viewMode === 'view' ? (
                        <button 
                            onClick={handleEnterSyncMode} 
                            disabled={isProcessing || !onUpdateActiveCart}
                            className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-800 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
                        >
                            <CartIcon className="w-4 h-4" />
                            {t('addToActive')}
                        </button>
                    ) : (
                        <button 
                            onClick={() => {
                                if (selectedItems.size === ingredients.length) setSelectedItems(new Set<string>());
                                else setSelectedItems(new Set(ingredients.map(i => i.name)));
                            }}
                            className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            {selectedItems.size === ingredients.length ? t('deselectAll') : t('selectAll')}
                        </button>
                    )}
                </div>

                <div className="p-6 overflow-y-auto flex-grow bg-slate-50/30 dark:bg-slate-900/30 scrollbar-none space-y-4">
                    {groupedIngredients.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 opacity-30">
                            <CartIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">This list is empty</p>
                        </div>
                    ) : (
                        groupedIngredients.map(([sectionName, items]) => {
                            if (hideSectionInViewMode) {
                                return (
                                    <div key={sectionName} className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                                        {renderIngredientRows(items)}
                                    </div>
                                );
                            }

                            const isCollapsed = collapsedSections.has(sectionName);
                            const allSectionSelected = isSectionFullySelected(items);

                            return (
                                <div key={sectionName} className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm transition-all duration-300 mb-4 last:mb-0">
                                    {isSelectionEnabled ? (
                                        <SelectionSectionHeader
                                            name={sectionName}
                                            itemCount={items.length}
                                            isCollapsed={isCollapsed}
                                            onToggle={() => toggleSectionVisibility(sectionName)}
                                            allSelected={allSectionSelected}
                                            onToggleSelectAll={() => toggleSelectSection(items)}
                                            t={t}
                                        />
                                    ) : (
                                        <EditableSectionHeader
                                            name={sectionName}
                                            itemCount={items.length}
                                            onRename={(newName) => handleRenameSection(sectionName, newName)}
                                            isCollapsed={isCollapsed}
                                            onToggle={() => toggleSectionVisibility(sectionName)}
                                            t={t}
                                        />
                                    )}

                                    {!isCollapsed && (
                                        <div className="animate-fade-in border-t border-slate-100 dark:border-slate-800">
                                            {renderIngredientRows(items)}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {viewMode !== 'view' && (
                    <div className="p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-4 shrink-0 shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.05)]">
                        {viewMode === 'sync' ? (
                            <div className="flex flex-col gap-3">
                                <button 
                                    onClick={handlePerformSync} 
                                    disabled={isProcessing || !onUpdateActiveCart}
                                    className="w-full py-4 bg-slate-900 dark:bg-slate-700 text-white rounded-2xl font-black text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-3 hover:bg-slate-800 dark:hover:bg-slate-600 transition-all shadow-xl shadow-slate-200 dark:shadow-slate-900/50 active:scale-[0.98] disabled:opacity-20"
                                >
                                    {isProcessing ? '...' : <><CartIcon className="w-5 h-5" /> {t('addToActive').toUpperCase()}</>}
                                </button>
                                <button onClick={handleCancelMode} className="text-slate-400 dark:text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-700 dark:hover:text-slate-300 py-2 text-center">{t('cancel')}</button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {selectedItems.size > 0 ? (
                                    <div className="grid grid-cols-3 gap-2 animate-fade-in">
                                        <button 
                                            onClick={() => { setSingleTargetIdx(null); setIsSectionChooserOpen(true); }}
                                            disabled={isProcessing}
                                            className="py-4 bg-sky-600 dark:bg-sky-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex flex-col items-center justify-center gap-1 hover:bg-sky-700 dark:hover:bg-sky-600 transition-all shadow-lg active:scale-95"
                                        >
                                            <ExportIcon className="w-4 h-4 transform rotate-90" />
                                            <span>{t('section')}</span>
                                        </button>
                                        <button 
                                            onClick={() => { setSingleTargetIdx(null); setIsListChooserOpen(true); }}
                                            disabled={isProcessing}
                                            className="py-4 bg-indigo-600 dark:bg-indigo-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex flex-col items-center justify-center gap-1 hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all shadow-lg active:scale-95"
                                        >
                                            <ListBulletIcon className="w-4 h-4" />
                                            <span>{t('list')}</span>
                                        </button>
                                        <button 
                                            onClick={() => setConfirmAction({ type: 'delete_items' })}
                                            disabled={isProcessing}
                                            className="py-4 bg-red-500 dark:bg-red-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex flex-col items-center justify-center gap-1 hover:bg-red-600 dark:hover:bg-red-700 transition-all shadow-lg active:scale-95"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                            <span>{t('delete')}</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-slate-300 dark:text-slate-600 text-[10px] font-black uppercase tracking-[0.2em] animate-fade-in">
                                        {t('nothingSelected')}
                                    </div>
                                )}
                                <button onClick={handleCancelMode} className="text-slate-400 dark:text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-700 dark:hover:text-slate-300 py-2 text-center">{t('cancel')}</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modals ... */}
            <ConfirmationModal 
                isOpen={!!confirmAction && confirmAction.type === 'delete_items'} 
                title={t('delete')} 
                message={singleTargetIdx !== null
                    ? t('confirmDeleteNamedItem').replace('{name}', ingredients[singleTargetIdx]?.name ?? '')
                    : t('confirmDeleteItemCount').replace('{count}', String(selectedItems.size))} 
                confirmText={t('delete')}
                cancelText={t('cancel')}
                onConfirm={handleConfirmedBulkDelete} 
                onCancel={() => {
                    setConfirmAction(null);
                    if (viewMode === 'view') setSingleTargetIdx(null);
                }} 
            />

            <ConfirmationModal 
                isOpen={!!confirmAction && confirmAction.type === 'delete_section'} 
                title={t('delete')} 
                message={t('confirmDeleteSection').replace(
                    '{section}',
                    confirmAction?.target ? getSectionDisplayName(confirmAction.target, t) : '',
                )} 
                confirmText={t('delete')}
                cancelText={t('cancel')}
                onConfirm={handleConfirmedSectionDelete} 
                onCancel={() => setConfirmAction(null)} 
            />

            <ConfirmationModal 
                isOpen={showDeleteCartConfirm} 
                title={t('deleteCart')} 
                message={t('confirmDeleteCart').replace('{name}', cart.name)} 
                confirmText={t('delete')}
                cancelText={t('cancel')}
                onConfirm={handleConfirmedDeleteCart} 
                onCancel={() => setShowDeleteCartConfirm(false)} 
            />

            <SectionChooserModal 
                isOpen={isSectionChooserOpen} 
                onClose={() => { setIsSectionChooserOpen(false); setSingleTargetIdx(null); }}
                sections={availableSections}
                onConfirm={handleMoveToSection}
                language={language}
            />

            <ListChooserModal 
                isOpen={isListChooserOpen}
                onClose={() => { setIsListChooserOpen(false); setSingleTargetIdx(null); }}
                carts={allCarts}
                currentCartId={cart.id}
                onConfirm={handleMoveToList}
                language={language}
            />
        </div>
    );
};

export default ShoppingCartDetailsModal;
